import { Injectable, NgZone } from "@angular/core";
import { LocalStoreService } from "../../shared/services/local-store.service";
import { Router, ActivatedRoute } from "@angular/router";
import { BehaviorSubject, EMPTY, Observable, Subject, of } from "rxjs";
import { environment } from "environments/environment";
import { LoginCredentials } from "../../models/index";
import { catchError, finalize, map, shareReplay, take } from "rxjs/operators";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { Store } from "@ngxs/store";
import jwt_decode from 'jwt-decode';
import { SSOLogin } from "app/models/auth/ssoLogin";
import { AuthorizationData, ClaimsData, UserRoleData } from "app/models/auth/claimsData";
import { Logout } from "app/root-store/auth/auth.action";
import { AutentifikacijaClient, KorisnikLoginResponse, KorisnikPrijava } from "app/api/uk-api-reference";
import { OperacijeKorisnikaService } from "./operacije-korisnika.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { OperacijeNadKompanijama } from "app/models/auth/operacijeKorisnikaNadKompanijama";
import { inflateRaw } from "pako";

@Injectable({
  providedIn: "root",
})
export class JwtAuthService {
  isAuthenticated: Boolean;
  user: KorisnikLoginResponse;

  user$ = (new BehaviorSubject<KorisnikLoginResponse>(null));
  claims = new BehaviorSubject<AuthorizationData[]>(null);
  claims$ = this.claims.asObservable();
  failedLoginSource = new Subject<SSOLogin>();
  failedLogin$ = this.failedLoginSource.asObservable();

  return: string;
  pomUser: any;
  JWT_TOKEN = "JWT_TOKEN";
  JWT_TOKEN_REFRESH = "JWT_TOKEN_REFRESH";
  APP_USER = "EGRET_USER";
  private refreshTokenInProgress$: Observable<KorisnikLoginResponse> | null = null;

  constructor(
    private ls: LocalStoreService,
    private router: Router,
    private route: ActivatedRoute,
    private ngxLoaderService: NgxUiLoaderService,
    private store: Store,
    private autentifikacijaClient: AutentifikacijaClient,
    private operacijeKorisnikaService: OperacijeKorisnikaService,
    private zone: NgZone,
    private matSnack: MatSnackBar,
    private translate: TranslateService
  ) {
    this.route.queryParams
      .subscribe(params => this.return = params['return'] || '/');
  }

  public Login(cred: LoginCredentials | null): Observable<KorisnikLoginResponse> {
    let obs: Observable<KorisnikLoginResponse>;
    if (cred != null && !environment.winAuth) {
      let korisnikPrijava = new KorisnikPrijava({
        korisnickoIme: cred.username,
        lozinka: cred.password,
        audience: environment.ssoConfig.clientId
      })
      obs = this.autentifikacijaClient.prijavaKorisnickoImeLozinka(korisnikPrijava, true);
    }
    else {
      let credentials = {} as KorisnikPrijava;
      obs = this.autentifikacijaClient.prijavaKorisnickoImeLozinka(credentials, true);
    }
    return obs.pipe(map(authRes => {
      this.setAuthorizedUserResponse(authRes);
      this.ngxLoaderService.stop();
      return authRes;
    }));
  }

  public LogOut(noRedirectToUrl: boolean = false): void {
    this.isAuthenticated = false;
    this.store.dispatch(new Logout(noRedirectToUrl));
    this.user$.next(null);

    this.user = {} as KorisnikLoginResponse;

    this.setUserAndToken(null);
    this.ls.clear();
  }

  setToken(token: string) {
    this.isAuthenticated = true;
    if (!!token) {
      this.ls.setItem(this.JWT_TOKEN, token);

      let tokenData = jwt_decode(token) as ClaimsData;
      this.claims.next(tokenData["Authorization claim"].Autorizacije);
    }
  }

  public RefreshToken(): Observable<KorisnikLoginResponse> {
    if (this.refreshTokenInProgress$) {
      return this.refreshTokenInProgress$;
    }

    this.refreshTokenInProgress$ = this.autentifikacijaClient.osveziToken(true).pipe(
      map(data => {
        if (data) {
          localStorage.setItem(this.APP_USER, JSON.stringify(data));
          this.setUserAndToken(data);
          this.user$.next(data);
          this.user = data;
        }
        return this.user;
      }),
      catchError((ex) => {
        // Cross-tab recovery: u slucaju da je drugi tab osvezio token
        if (this.isTokenValid()) {
          const userData = this.ls.getItem(this.APP_USER);
          if (userData) {
            const user: KorisnikLoginResponse = typeof userData === 'string' ? JSON.parse(userData) : userData;
            this.setUserAndToken(user);
            this.user$.next(user);
            this.user = user;
            return of(user);
          }
        }
        this.translate.get('errors.isteklaSesija').pipe(take(1)).subscribe(msg => {
          this.zone.run(() => {
            this.matSnack.open(msg, null, { duration: 5000, panelClass: ["errorSnack"] });
          });
        });
        this.zone.run(() => {
          console.error(ex);
          this.signout();
        })
        return EMPTY;
      }),
      shareReplay(1),
      finalize(() => { this.refreshTokenInProgress$ = null; })
    );

    return this.refreshTokenInProgress$;
  }

  isTokenValid(): Boolean {
    var oldToken = this.getJwtToken();
    if (!oldToken) return false;
    else {
      const decoded = jwt_decode(oldToken) as any;
      const expiryDate = new Date(decoded.exp * 1000);
      return expiryDate > new Date();
    }
  }

  public signout() {
    this.setUserAndToken(null);
    this.ls.clear();
    this.router.navigate(["session/signin"]);
    this.LogOut();
  }

  isLoggedIn(): Boolean {
    return !!this.getJwtToken();
  }

  getJwtToken() {
    return this.ls.getItem(this.JWT_TOKEN);
  }

  getUser() {
    return this.ls.getItem(this.APP_USER);
  }

  getErgetUser() {
    return this.ls.getItem(this.APP_USER);
  }

  setAuthorizedUserResponse(user: KorisnikLoginResponse) {
    this.user = user;
    this.user$.next(user);
    this.isAuthenticated = true;
    this.setUserAndToken(this.user);
    this.zone.run(() => {
      if (!!sessionStorage.getItem("logovanjeSaServisa") && sessionStorage.getItem("logovanjeSaServisa") == "true") {
        sessionStorage.removeItem("logovanjeSaServisa");
        this.router.navigate(['/recnik-poruka/nova-poruka']);
      } else {
        this.router.navigate(['']);
      }
    });
  }

  setUserAndToken(user: KorisnikLoginResponse) {
    let token = user != null ? user.token : null;
    this.setToken(token);

    this.ls.setItem(this.APP_USER, user);
    this.ls.setItem(this.JWT_TOKEN_REFRESH, user ? user.refreshToken : null);

    if (token) {
      let authorizationClaim = this.getAutorizationClaim();
      this.operacijeKorisnikaService.kesirajOperacijeKorisnikaNadKompanijama(authorizationClaim);
    } else {
      this.operacijeKorisnikaService.ocistiKesOperacijaSaKompanijama();
    }
  }

  /**
  * Dekodira JWT i vraca claim za autorizacije koji sadrzi operacije nad kompanijama
  * @returns AuthorizationDataWrapper
  */
  getAutorizationClaim(): OperacijeNadKompanijama[] {
    try {
      let tokenData = jwt_decode(this.ls.getItem(this.JWT_TOKEN)) as ClaimsData;
      let authorizationDeflated = tokenData["Authorization claim"];
      let res = this.decode(authorizationDeflated);
      return JSON.parse(res);
    }
    catch (e) {
      console.log(e);
    }
  }

  // TODO: OBRISATI OVE NEPOTREBNE METODE DA NE ZBUNJUJU !!!
  getClaimData(): AuthorizationData[] {
    let tokenData = jwt_decode(this.ls.getItem(this.JWT_TOKEN)) as ClaimsData;
    this.claims.next(tokenData["Authorization claim"].Autorizacije);
    return tokenData["Authorization claim"].Autorizacije;
  }

  hasClaim(data: { operacije: string[], mustMatchAll?: boolean }) {
    return true;
  }

  /**
   * Provera da li korisnik za odredjenu kopaniju (organ/oj) ima odredjenu operaciju
   * @param data 
   * operacije -> koje su potrebne za akciju, 
   * sifraOrgana -> kompanija za koju se dozvoljava operqacija
   * mustMatchAll -> true ako su korisnik treba da ima sve zahtevane operacije
   * @returns 
   */
  hasOperationForCompany(data: { operacije: string[], sifraOrgana: string, mustMatchAll?: boolean }): boolean {
    return true;
  }

  /**
   * Inflate (Decompress) deflated (compressed) base64 string.
   * Claim za autorizacije je kompresovan na backu zbog velicine, ovde ga dekompresujemo.
   * @param data 
   * @returns 
   */
  decode(data) {
    try {
      data = atob(data);
    }
    catch (e) {
      console.error('atob failed: ' + e);
      return;
    }
    try {
      data = inflateRaw(Uint8Array.from(data, (c: any) => c.charCodeAt(0)), { to: 'string' });
    }
    catch (e) {
      console.error('inflateRaw failed: ' + e);
      return;
    }
    try {
      data = decodeURIComponent(data);
    }
    catch (e) {
      console.error('decodeURIComponent failed: ' + e);
      return;
    }
    if (data.length > 0) {
      return data;
    }
  };

  /**
   * Da li ulogovani korisnik ima operaciju nad svim entitetima.
   * Ako ima nad hardkodiranim organom sa ID = 1 (IOP & MR) onda se racuna kao da ima
   * operaciju nad svim organima i oj.
   * @param operacija Naziv operacije
   * @returns true/false
   */
  korisnikImaOperacijuGlobalno(operacija: string): boolean {
    return this.operacijeKorisnikaService.korisnikImaOperacijuGlobalno(operacija);
  }

  /**
   * Da li ulogovani korisnik ima operaciju nad bilo kojim entitetom. Bilo da ima
   * globalno operaciju, ili nad nekim organom ili OJ, vraca se true.
   * @param operacija Naziv operacije
   * @returns true/false
   */
  korisnikImaOperacijuNadBiloKojimEntitetom(operacija: string): boolean {
    return this.operacijeKorisnikaService.korisnikImaOperacijuNadBiloKojimEntitetom(operacija);
  }

  /**
   * Da li ulogovani korisnik ima operaciju nad prosledjenim org entitetom.
   * Ako ima operaciju globalno, smatra se da ima i nad entitetom.
   * U suprotnom se proverava da li ima operaciju nad bas tim entitetom, ili nekim entitetom IZNAD njega? (TODO)
   * @param operacija 
   * @param sifraOrgEntiteta 
   */
  korisnikImaOperacijuNadOrganizacionimEntitetom(operacija: string, sifraOrgEntiteta: string): boolean {
    return this.operacijeKorisnikaService.korisnikImaOperacijuNadOrganizacionimEntitetom(operacija, sifraOrgEntiteta);
  }

  korisnikImaListuOperacijaNadEntitetom(operacije: string[], sifraOrgEntiteta: string) {
    return this.operacijeKorisnikaService.korisnikImaListuOperacijaNadEntitetom(operacije, sifraOrgEntiteta);
  }

  korisnikImaListuOperacijaGlobalno(operacije: string[]) {
    return this.operacijeKorisnikaService.korisnikImaListuOperacijaGlobalno(operacije);
  }
}
